import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { PhoneInput } from "components/designV2/phoneInput/phoneInput";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import {
  FieldsGrid,
  FooterWrapper,
  SubmitErrorText,
} from "./addStaffPanel.styled";
import type { StampClockRegistrationPanelProps } from "./stampClockRegistration.contract";
import { useStampClockRegistrationLogic } from "./stampClockRegistration.logic";

const PANEL_TITLE_ID = "stamp-clock-registration-panel-title";

/**
 * Lets a person the stamp clock does not know register themselves. Only the
 * personal details and a team are asked for.
 */
export const StampClockRegistrationPanel = (
  props: StampClockRegistrationPanelProps,
): JSX.Element => {
  const { t } = useTranslation();
  const onClose = props.onClose;
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const logic = useStampClockRegistrationLogic(props);
  const { fieldErrors, formData, setField } = logic;
  const isPhoneNumberLocked = !!props.phoneNumber;
  const isPersonalIdentityNumberLocked = !!props.personalIdentityNumber;
  const toMandatoryLabel = (label: string): string =>
    `${label} (${t("Staff.AddStaffPanelMandatory")})`;
  const toErrorText = (key: string | null): string => (key ? t(key) : "");

  const closePanel = useCallback((): void => {
    setIsDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback((): void => {
    if (logic.isSubmitting) return;
    if (logic.isFormStarted) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    closePanel();
  }, [closePanel, logic.isFormStarted, logic.isSubmitting]);

  useEffect(() => {
    if (!logic.isFormStarted) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [logic.isFormStarted]);

  useCloseOnEscape({
    isOpen: !logic.isSubmitting && !isDiscardConfirmOpen,
    onClose: requestClose,
    stopPropagation: true,
    preventDefault: true,
  });

  return (
    <>
      <Prompt
        when={logic.isFormStarted}
        message={t("ProfileCompany.YouHaveUnSavedChanges")}
      />
      <SidePanel
        isOpen={true}
        onClose={requestClose}
        onBackdropClick={requestClose}
        title={t("Staff.AddStaffPanelStampClockTitle")}
        titleId={PANEL_TITLE_ID}
        footer={
          <FooterWrapper>
            {logic.submitError.length > 0 && (
              <SubmitErrorText role="alert">
                {logic.submitError}
              </SubmitErrorText>
            )}
            <PrimaryButton
              label={t("Staff.AddStaffPanelStampClockRegister")}
              disabled={logic.isSubmitDisabled}
              isLoading={logic.isSubmitting}
              onClick={logic.onSubmitClick}
            />
          </FooterWrapper>
        }
      >
        <FieldsGrid>
          <PhoneInput
            fullWidth
            label={t("Staff.AddStaffPanelFieldPhoneNumberMandatory")}
            value={formData.phoneNumber}
            disabled={logic.isSubmitting || isPhoneNumberLocked}
            hasError={fieldErrors.phoneNumber != null}
            errorText={toErrorText(fieldErrors.phoneNumber)}
            onChange={(value) => setField("phoneNumber", value)}
          />
          <InputField
            fullWidth
            label={toMandatoryLabel(
              t("Staff.AddStaffPanelFieldPersonalNumber"),
            )}
            hintText={t("Staff.AddStaffPanelHintPersonalNumberFormat")}
            value={formData.personalIdentityNumber}
            disabled={logic.isSubmitting || isPersonalIdentityNumberLocked}
            hasError={fieldErrors.personalIdentityNumber != null}
            errorText={toErrorText(fieldErrors.personalIdentityNumber)}
            onChange={(value) => setField("personalIdentityNumber", value)}
          />
          <InputField
            fullWidth
            label={t("Staff.AddStaffPanelFieldFirstNameMandatory")}
            value={formData.firstName}
            disabled={logic.isSubmitting}
            hasError={fieldErrors.firstName != null}
            errorText={toErrorText(fieldErrors.firstName)}
            onChange={(value) => setField("firstName", value)}
          />
          <InputField
            fullWidth
            label={t("Staff.AddStaffPanelFieldLastNameMandatory")}
            value={formData.lastName}
            disabled={logic.isSubmitting}
            hasError={fieldErrors.lastName != null}
            errorText={toErrorText(fieldErrors.lastName)}
            onChange={(value) => setField("lastName", value)}
          />
          <SearchableDropdown
            fullWidth
            label={toMandatoryLabel(t("Staff.AddStaffPanelFieldTeam"))}
            options={logic.teamOptions}
            value={logic.teamId}
            disabled={logic.isSubmitting}
            onChange={(value) => setField("teamId", value)}
            onClear={() => setField("teamId", "")}
            placeholder={t("Staff.AddStaffPanelPlaceholderNotAssigned")}
          />
        </FieldsGrid>
      </SidePanel>
      <Modal
        variant="confirm"
        label={t("Staff.AddStaffPanelDiscardConfirmTitle")}
        size="compact"
        showCloseButton={false}
        isOpen={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        footer={
          <>
            <SecondaryButton
              label={t("General.Cancel")}
              onClick={() => setIsDiscardConfirmOpen(false)}
            />
            <PrimaryButton
              label={t("Staff.AddStaffPanelDiscardConfirmAction")}
              onClick={closePanel}
            />
          </>
        }
      >
        {t("Staff.AddStaffPanelDiscardConfirmBody")}
      </Modal>
    </>
  );
};
